import { html, nothing, type TemplateResult } from "lit";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import type { AssistantIdentity } from "../assistant-identity.ts";
import { icons } from "../icons.ts";
import type { MessageGroup } from "../types/chat-types.ts";
import {
  attachmentIconColorClassForAttachment,
  attachmentIconForRenderKind,
} from "./attachment-icon-color.ts";
import { openAttachmentPreviewOverlay } from "./attachment-preview-overlay.ts";
import {
  attachmentIsPreviewable,
  attachmentKindForMime,
  type AttachmentKind,
  type ChatAttachment,
} from "./attachment-types.ts";
import { applySeek, renderAudioProgressBar, updateAudioProgressBar } from "./audio-progress-bar.ts";
import {
  attachmentEligibleForInlinePreview,
  type InlinePreviewFile,
  type InlinePreviewOptions,
} from "./chat-attachment-inline-preview.ts";
import {
  isFileChangesMessage,
  parseFileChangesPayload,
  renderChatFileChangesSummary,
} from "./chat-file-changes-summary.ts";
import { renderExpandableMessageBody } from "./chat-message-expand.ts";
import { userMessageGroupPlainText } from "./chat-pinned-question.ts";
import { extractConversationSummary } from "./chat-transcript-filters.ts";
import { renderCopyAsMarkdownButton } from "./copy-as-markdown.ts";
import { requestAttachmentSeek, resolveAttachmentPlaybackTime } from "./media-playback-state.ts";
import { renderMessageActions, type MessageActionHandlers } from "./message-actions.ts";
import {
  extractAssistantErrorMessage,
  extractTextCached,
  extractThinkingCached,
} from "./message-extract.ts";
import { isToolResultMessage, normalizeRoleForGrouping } from "./message-normalizer.ts";
import {
  isAssistantMessage,
  isUserMessage,
  type MessageRewindTarget,
  rewindTargetsEqual,
} from "./message-transcript.ts";
import { renderReasoningBlock } from "./reasoning-block.ts";
import { formatAttachmentBytes, mediaTypeLabel } from "./session-files.ts";
import { renderToolCardSidebar } from "./tool-cards.ts";
import { extractToolCards } from "./tool-cards.ts";
import { renderToolRow, shouldUseToolRow } from "./tool-rows.ts";

type ImageBlock = {
  url: string;
  alt?: string;
};

type AudioBlock = {
  url: string;
  mimeType?: string;
  /**
   * JAISIU-4447 — stable key for the pending-seek state.
   *
   * The transcript audio player reconciles the browser's reported
   * position against a PENDING seek (the same contract the overlay
   * uses), so the element needs a stable id to key that state on.
   * Derived from the URL when the upstream block does not carry one.
   */
  id?: string;
};

/**
 * A session file referenced by a user message.
 *
 * Written onto the persisted user row by the IronLoop dispatch
 * (`message.sessionFiles`) so the attachment is still there after a reload
 * and still addressable from a later turn. Metadata only — the bytes stay
 * on the gateway.
 */
/**
 * JAISIU-4435 — resolve the render kind for a transcript session file.
 *
 * NOTE: the canonical implementation is the exported
 * {@link transcriptFileRenderKind} further down this file. It already
 * carries the PDF-beats-`document` rule (the gateway calls a PDF
 * `document`; the MIME `application/pdf` wins), so the JAISIU-4435
 * variant is not duplicated here.
 */

/**
 * JAISIU-4148 / JAISIU-4427 — pick the preview URL from the resolver
 * when the host supplies one; otherwise use the raw URL.
 *
 * ## The preview 404 is real (defect D5)
 *
 * `writePreviewDerivative` in the gateway returns `null` for any
 * non-image kind, and also for images when `sharp` is unavailable or
 * fails to decode. The `/preview` route then answers 404. So a chip
 * whose `<img src>` is unconditionally the preview URL renders as a
 * broken image for exactly the files the operator most wants to see.
 *
 * We therefore keep the thumbnail on the resolver but always install
 * an `onerror` that rewrites `src` to the raw bytes. The chip is
 * styled to the same 44×44 box either way, so the fallback is
 * visually silent.
 */
export type TranscriptSessionFile = {
  fileId: string;
  filename: string;
  mediaType: string;
  size: number;
  sha256?: string;
  kind?: string;
  /**
   * JAISIU-4428 — set by the gateway's `serialize()` only when it
   * actually wrote a preview derivative (`SessionFileRecord.previewMediaType`).
   * `GET /api/session/files/<id>/preview` 404s when this is absent, so
   * this flag is what lets the chip pick a URL that will resolve instead
   * of guessing. See {@link resolveSessionFilePreviewSrc}.
   */
  previewMediaType?: string;
};

/**
 * Read the session-file refs off a transcript message.
 *
 * Exported for tests: a user bubble with an attachment must carry these, and
 * the Files tab cross-links to the same ids.
 */
export function extractSessionFiles(message: unknown): TranscriptSessionFile[] {
  const raw = (message as { sessionFiles?: unknown } | null | undefined)?.sessionFiles;
  if (!Array.isArray(raw)) return [];
  const out: TranscriptSessionFile[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const rec = entry as Record<string, unknown>;
    if (typeof rec.fileId !== "string" || !rec.fileId) continue;
    out.push({
      fileId: rec.fileId,
      filename: typeof rec.filename === "string" ? rec.filename : "file",
      mediaType: typeof rec.mediaType === "string" ? rec.mediaType : "",
      size: typeof rec.size === "number" ? rec.size : 0,
      ...(typeof rec.sha256 === "string" ? { sha256: rec.sha256 } : {}),
      ...(typeof rec.kind === "string" ? { kind: rec.kind } : {}),
      ...(typeof rec.previewMediaType === "string"
        ? { previewMediaType: rec.previewMediaType }
        : {}),
    });
  }
  return out;
}

/**
 * JAISIU-3993: read the OUTBOUND session-file refs off a transcript
 * message. These are files the AGENT produced in this turn via a tool
 * (gen-ai image / video / audio — see `attachMediaToSession` in
 * `src/agents/tools/lib/attach-media-to-session.ts`). They are written
 * onto the assistant's `message.outboundAttachments` by the iron-loop
 * dispatch when the turn finalises, so the chip survives reloads.
 *
 * Schema mirrors the inbound `extractSessionFiles` shape above, with
 * one extra field (`dataFileId` is not used here; the file id is
 * already the same value the Files tab exposes).
 */
export function extractOutboundAttachments(message: unknown): TranscriptSessionFile[] {
  const raw = (message as { outboundAttachments?: unknown } | null | undefined)
    ?.outboundAttachments;
  if (!Array.isArray(raw)) return [];
  return extractSessionFiles({ sessionFiles: raw });
}

/** Exported for unit tests (Iron flat image blocks). */
export function extractMedias(message: unknown): { images: ImageBlock[]; audios: AudioBlock[] } {
  const m = message as Record<string, unknown>;
  const content = m.content;
  const images: ImageBlock[] = [];
  const audios: AudioBlock[] = [];

  if (Array.isArray(content)) {
    for (const block of content) {
      if (typeof block !== "object" || block === null) {
        continue;
      }
      const b = block as Record<string, unknown>;

      if (b.type === "image") {
        // Handle source object format (from sendChatMessage)
        const source = b.source as Record<string, unknown> | undefined;
        if (source?.type === "base64" && typeof source.data === "string") {
          const data = source.data;
          const mediaType = (source.media_type as string) || "image/png";
          // If data is already a data URL, use it directly
          const url = data.startsWith("data:") ? data : `data:${mediaType};base64,${data}`;
          images.push({ url });
        } else if (typeof b.data === "string" && b.data.trim()) {
          // Iron / gateway flat block: { type, data, mimeType } (JAISIU-3789)
          const data = b.data.trim();
          const mediaType = (b.mimeType as string) || (b.media_type as string) || "image/png";
          const url = data.startsWith("data:") ? data : `data:${mediaType};base64,${data}`;
          images.push({ url });
        } else if (typeof b.url === "string") {
          images.push({ url: b.url });
        }
      } else if (b.type === "image_url") {
        // OpenAI format
        const imageUrl = b.image_url as Record<string, unknown> | undefined;
        if (typeof imageUrl?.url === "string") {
          images.push({ url: imageUrl.url });
        }
      } else if (b.type === "audio") {
        // Handle audio blocks (from audio attachments)
        const source = b.source as Record<string, unknown> | undefined;
        if (source?.type === "base64" && typeof source.data === "string") {
          const data = source.data;
          const mediaType = (source.media_type as string) || "audio/mp3";
          const url = data.startsWith("data:") ? data : `data:${mediaType};base64,${data}`;
          audios.push({ url, mimeType: mediaType });
        } else if (typeof b.data === "string" && b.data.trim()) {
          const data = b.data.trim();
          const mediaType = (b.mimeType as string) || (b.media_type as string) || "audio/mp3";
          const url = data.startsWith("data:") ? data : `data:${mediaType};base64,${data}`;
          audios.push({ url, mimeType: mediaType });
        } else if (typeof b.url === "string") {
          audios.push({ url: b.url, mimeType: b.mimeType as string | undefined });
        }
      }
    }
  }

  return { images, audios };
}

/** @deprecated Use extractMedias instead */
function extractImages(message: unknown): ImageBlock[] {
  return extractMedias(message).images;
}

export function renderReadingIndicatorGroup(assistant?: AssistantIdentity) {
  return html`
    <div class="chat-group assistant">
      ${renderAvatar("assistant", assistant)}
      <div class="chat-group-messages">
        <div class="chat-bubble chat-reading-indicator" aria-hidden="true" data-testid="agent-busy">
          <span class="chat-reading-indicator__dots">
            <span></span><span></span><span></span>
          </span>
        </div>
      </div>
    </div>
  `;
}

export function renderStreamingGroup(
  text: string,
  startedAt: number,
  onOpenSidebar?: (content: string) => void,
  assistant?: AssistantIdentity,
  onOpenViewer?: (markdown: string, messageId: string) => void,
) {
  const timestamp = new Date(startedAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const name = assistant?.name ?? "Assistant";

  return html`
    <div class="chat-group assistant">
      ${renderAvatar("assistant", assistant)}
      <div class="chat-group-messages">
        ${renderGroupedMessage(
          {
            role: "assistant",
            content: [{ type: "text", text }],
            timestamp: startedAt,
          },
          { isStreaming: true, showReasoning: false, onOpenViewer },
          onOpenSidebar,
        )}
        <div class="chat-group-footer">
          <span class="chat-sender-name">${name}</span>
          <span class="chat-group-timestamp">${timestamp}</span>
        </div>
      </div>
    </div>
  `;
}

/**
 * JAISIU-3473 — extract a stable id from the FIRST message in a group.
 * Returns `undefined` when no message in the group carries an id (the
 * scroll-restoration logic then falls back to position math).
 */
// JAISIU-3551 (operator follow-up) — do not offer a collapse control on
// one-liners: there's nothing to collapse when the body fits in a single
// line of prose, and a clickable avatar with no effect is just noise.
// Threshold: ≥3 lines OR a single line of ≥120 chars. The single-line
// escape hatch covers wide ASCII-art / status banners that wrap visually
// but contain no newlines.
export const COLLAPSIBLE_MIN_LINES = 3;
export const COLLAPSIBLE_MIN_CHARS = 120;

/**
 * Text-based collapsible check, shared between the grouped-message path
 * (`renderMessageGroup` → `isCollapsibleGroup`) and the assistant-turn
 * path (`renderAssistantTurnGroup` → final-answer text). Pure function
 * so it's safe to call outside a renderer context.
 */
export function isCollapsibleText(text: string | null | undefined): boolean {
  const t = (text ?? "").trim();
  if (!t) return false;
  const lineCount = t.split(/\n/).length;
  if (lineCount >= COLLAPSIBLE_MIN_LINES) return true;
  return t.length >= COLLAPSIBLE_MIN_CHARS;
}

function isCollapsibleGroup(group: MessageGroup): boolean {
  const first = group.messages[0]?.message;
  if (!first || typeof first !== "object") return false;
  return isCollapsibleText(extractTextCached(first));
}

function firstMessageIdOfGroup(group: MessageGroup): string | undefined {
  for (const item of group.messages) {
    const message = item.message as unknown;
    if (!message || typeof message !== "object") continue;
    const record = message as { id?: unknown; transcriptId?: unknown; messageId?: unknown };
    for (const key of ["id", "transcriptId", "messageId"] as const) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }
  return undefined;
}

export function renderMessageGroup(
  group: MessageGroup,
  opts: {
    onOpenSidebar?: (content: string) => void;
    onOpenViewer?: (markdown: string, messageId: string) => void;
    showReasoning: boolean;
    reasoningExpanded?: boolean;
    transcriptMode?: boolean;
    assistantName?: string;
    assistantAvatar?: string | null;
    messageActions?: MessageActionHandlers;
    isLastAssistantGroup?: boolean;
    resolveRewindTarget?: (message: unknown) => MessageRewindTarget | null;
    editingRewindTarget?: MessageRewindTarget | null;
    onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
    /** Render an inline fleet-question card attached to the bottom of this group
     * (used only on the last assistant group, so the card visually follows the
     * assistant's final answer instead of floating in mid-thread). */
    inlineFleetQuestion?: unknown;
    /** JAISIU-3551 — set of message ids currently in the manually-collapsed
     * state (see `renderGroupedMessage` for semantics). */
    collapsedMessageIds?: ReadonlySet<string>;
    /** JAISIU-3551 — toggle handler invoked when the avatar is clicked. */
    onToggleMessageCollapsed?: (messageId: string) => void;
    /** JAISIU-XXXX — transient anchor highlight applied to the
     * scroll-restored group on a warm session switch. CSS owns the
     * 700 ms wash; the renderer just flips the `chat-group--anchor-flash`
     * class on the matching group. */
    anchorHighlightMessageId?: string | null;
    /**
     * Resolve a stored session file to its gateway URL. Host-supplied so
     * this module stays free of credentials and origin knowledge.
     */
    resolveSessionFileUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4148 — resolve a stored session file to its gateway
     * **preview** URL (downscaled thumbnail). Used for the `<img src>` on
     * image chips so the thumbnail comes from a URL the resolver
     * actually returns, not a `/raw`→`/preview` string-replace that
     * breaks on signed/CDN/non-default URL shapes. Falls back to
     * `resolveSessionFileUrl` when omitted (backwards compatible).
     */
    resolveSessionFilePreviewUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4449 — load the *prepared preview* of a stored session
     * file (tabular schema + sample rows for a spreadsheet, extracted
     * text for a document). Host-supplied so this module stays free of
     * credentials/origin knowledge. Optional — without it the overlay
     * keeps the honest "not analysed" message.
     */
    loadSessionFilePreview?: (file: TranscriptSessionFile) => Promise<ChatAttachment | null>;
  },
) {
  const normalizedRole = normalizeRoleForGrouping(group.role);
  const assistantName = opts.assistantName ?? "Assistant";
  const summaryOnlyGroup =
    (normalizedRole === "user" || normalizedRole === "system") &&
    group.messages.length > 0 &&
    group.messages.every((item) => {
      const text = extractTextCached(item.message)?.trim() ?? "";
      if (!text) {
        return false;
      }
      const parts = extractConversationSummary(text);
      return Boolean(parts.summary) && !parts.body.trim();
    });
  const who =
    normalizedRole === "user"
      ? "You"
      : normalizedRole === "assistant"
        ? assistantName
        : normalizedRole === "system"
          ? "System"
          : normalizedRole;
  const roleClass = summaryOnlyGroup
    ? "dropped-summary"
    : normalizedRole === "user"
      ? "user"
      : normalizedRole === "assistant"
        ? "assistant"
        : normalizedRole === "system"
          ? "system"
          : "other";
  const timestamp = new Date(group.timestamp).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  const userTurnText =
    normalizedRole === "user" && !summaryOnlyGroup ? userMessageGroupPlainText(group) : "";

  // JAISIU-3473 — anchor for upward-pagination scroll restoration.
  // The first message in the group carries the group's stable id;
  // when the group has no id (rare — e.g. a transient activity row
  // without a transcript id), the attribute is omitted and the
  // scroll-restoration logic falls back to position-math.
  const groupAnchorId = firstMessageIdOfGroup(group);
  const anchorHighlight =
    opts.anchorHighlightMessageId && groupAnchorId === opts.anchorHighlightMessageId;

  return html`
    <div
      class="chat-group ${roleClass}${anchorHighlight ? " chat-group--anchor-flash" : ""}"
      ?data-chat-user-turn=${normalizedRole === "user" && !summaryOnlyGroup ? true : nothing}
      data-chat-user-text=${normalizedRole === "user" && !summaryOnlyGroup ? userTurnText : nothing}
      data-msg-id=${groupAnchorId ?? nothing}
    >
      ${
        opts.transcriptMode || summaryOnlyGroup
          ? nothing
          : renderAvatar(
              group.role,
              {
                name: assistantName,
                avatar: opts.assistantAvatar ?? null,
              },
              // JAISIU-3551 — avatar is the manual collapse control for the
              // first message in the group (same key as scroll-anchor).
              // Operator follow-up: do NOT offer the control on one-liners
              // (no content to collapse). Both assistant and user bubbles
              // are eligible; the role check is intentionally absent.
              groupAnchorId && opts.onToggleMessageCollapsed && isCollapsibleGroup(group)
                ? {
                    collapseToggleId: groupAnchorId,
                    onCollapseToggle: opts.onToggleMessageCollapsed,
                  }
                : undefined,
            )
      }
      <div class="chat-group-messages">
        ${group.messages.map((item, index) =>
          renderGroupedMessage(
            item.message,
            {
              isStreaming: group.isStreaming && index === group.messages.length - 1,
              showReasoning: opts.showReasoning,
              reasoningExpanded: opts.reasoningExpanded,
              transcriptMode: opts.transcriptMode,
              messageActions: summaryOnlyGroup ? undefined : opts.messageActions,
              rewindTarget: summaryOnlyGroup
                ? null
                : (opts.resolveRewindTarget?.(item.message) ?? null),
              editingRewindTarget: opts.editingRewindTarget ?? null,
              isLastAssistantInThread:
                Boolean(opts.isLastAssistantGroup) &&
                index === group.messages.length - 1 &&
                isAssistantMessage(item.message),
              onFileClick: opts.onFileClick,
              onOpenViewer: opts.onOpenViewer,
              collapsedMessageIds: opts.collapsedMessageIds,
              onToggleMessageCollapsed: opts.onToggleMessageCollapsed,
              resolveSessionFileUrl: opts.resolveSessionFileUrl,
              resolveSessionFilePreviewUrl: opts.resolveSessionFilePreviewUrl,
              loadSessionFilePreview: opts.loadSessionFilePreview,
            },
            opts.onOpenSidebar,
          ),
        )}
        ${
          // Attach the inline fleet-question card to the bottom of the LAST
          // assistant group only — visually "at the end of answer" rather
          // than floating mid-thread. Skip in transcript mode or for
          // non-assistant groups.
          opts.isLastAssistantGroup &&
          !opts.transcriptMode &&
          !summaryOnlyGroup &&
          opts.inlineFleetQuestion
            ? opts.inlineFleetQuestion
            : nothing
        }
        ${
          opts.transcriptMode || summaryOnlyGroup
            ? nothing
            : html`
                <div class="chat-group-footer">
                  <span class="chat-sender-name">${who}</span>
                  <span class="chat-group-timestamp">${timestamp}</span>
                </div>
              `
        }
      </div>
    </div>
  `;
}

export function renderAvatar(
  role: string,
  assistant?: Pick<AssistantIdentity, "name" | "avatar">,
  /**
   * JAISIU-3551 — when both `collapseToggleId` and `onCollapseToggle` are
   * present, the avatar becomes the manual collapse/expand control for that
   * message. The avatar itself is visually unchanged; only a subtle hover
   * state + `cursor: pointer` are added (see `.chat-avatar--clickable` in
   * `ui/src/styles/chat/grouped.css`). Do NOT add any new buttons or icons —
   * the avatar IS the control.
   */
  collapseToggle?: {
    collapseToggleId: string;
    onCollapseToggle: (id: string) => void;
  },
) {
  const normalized = normalizeRoleForGrouping(role);
  const assistantName = assistant?.name?.trim() || "Assistant";
  const assistantAvatar = assistant?.avatar?.trim() || "";
  const initial =
    normalized === "user"
      ? "U"
      : normalized === "assistant"
        ? assistantName.charAt(0).toUpperCase() || "A"
        : normalized === "system"
          ? "S"
          : normalized === "tool"
            ? "⚙"
            : "?";
  const className =
    normalized === "user"
      ? "user"
      : normalized === "assistant"
        ? "assistant"
        : normalized === "system"
          ? "system"
          : normalized === "tool"
            ? "tool"
            : "other";

  // JAISIU-3551 — when the avatar is the manual collapse control, attach
  // the click + keydown handlers directly to the avatar element (Lit's
  // event listeners MUST be on the element where they're declared).
  const extraClass = collapseToggle ? " chat-avatar--clickable" : "";
  const handleClick = (e: MouseEvent) => {
    // Stop propagation so the click doesn't bubble to the bubble's own
    // click handlers (e.g. "click-to-edit" on user bubbles).
    e.stopPropagation();
    e.preventDefault();
    collapseToggle?.onCollapseToggle(collapseToggle.collapseToggleId);
  };
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.stopPropagation();
    e.preventDefault();
    collapseToggle?.onCollapseToggle(collapseToggle.collapseToggleId);
  };

  if (assistantAvatar && normalized === "assistant") {
    if (isAvatarUrl(assistantAvatar)) {
      return collapseToggle
        ? html`<img
            class="chat-avatar ${className}${extraClass}"
            src="${assistantAvatar}"
            alt="${assistantName}"
            data-testid="chat-avatar-collapse-toggle"
            data-msg-id=${collapseToggle.collapseToggleId}
            role="button"
            tabindex="0"
            aria-label="Collapse or expand message"
            @click=${handleClick}
            @keydown=${handleKeydown}
          />`
        : html`<img
            class="chat-avatar ${className}"
            src="${assistantAvatar}"
            alt="${assistantName}"
          />`;
    }
    return collapseToggle
      ? html`<div
          class="chat-avatar ${className}${extraClass}"
          data-testid="chat-avatar-collapse-toggle"
          data-msg-id=${collapseToggle.collapseToggleId}
          role="button"
          tabindex="0"
          aria-label="Collapse or expand message"
          @click=${handleClick}
          @keydown=${handleKeydown}
        >
          ${assistantAvatar}
        </div>`
      : html`<div class="chat-avatar ${className}">${assistantAvatar}</div>`;
  }

  return collapseToggle
    ? html`<div
        class="chat-avatar ${className}${extraClass}"
        data-testid="chat-avatar-collapse-toggle"
        data-msg-id=${collapseToggle.collapseToggleId}
        role="button"
        tabindex="0"
        aria-label="Collapse or expand message"
        @click=${handleClick}
        @keydown=${handleKeydown}
      >
        ${initial}
      </div>`
    : html`<div class="chat-avatar ${className}">${initial}</div>`;
}

function isAvatarUrl(value: string): boolean {
  return (
    /^https?:\/\//i.test(value) || /^data:image\//i.test(value) || value.startsWith("/") // Relative paths from avatar endpoint
  );
}

/** Muted collapsible chip for prompt-budget / offloader older-turns summaries. */
function renderDroppedTurnsSummary(summary: string) {
  const preview =
    summary
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line && !line.startsWith("[Conversation summary") && line !== "[End summary]")
      ?.replace(/^[-*]\s*/, "")
      .slice(0, 96) ?? "Older turns compacted for context";

  return html`
    <details class="chat-dropped-summary">
      <summary class="chat-dropped-summary__summary">
        <span class="chat-dropped-summary__icon" aria-hidden="true">${icons.prism}</span>
        <span class="chat-dropped-summary__title">Older turns summary</span>
        <span class="chat-dropped-summary__preview muted"
          >${preview}${preview.length >= 96 ? "…" : ""}</span
        >
      </summary>
      <div class="chat-dropped-summary__body">
        <pre class="chat-dropped-summary__pre">${summary}</pre>
      </div>
    </details>
  `;
}

function renderMessageImages(images: ImageBlock[]) {
  if (images.length === 0) {
    return nothing;
  }

  return html`
    <div class="chat-message-images">
      ${images.map(
        (img) => html`
          <img
            src=${img.url}
            alt=${img.alt ?? "Attached image"}
            class="chat-message-image"
            @click=${() => openMessageImagePreview(img)}
          />
        `,
      )}
    </div>
  `;
}

/**
 * Open the unified preview overlay for a message-side image.
 *
 * `ImageBlock` is the transcript shape (url + alt); we map it to
 * the narrow `ChatAttachment` the overlay expects. Post-send mode
 * means the download button is visible (the file is on the
 * server, the operator can re-download it).
 *
 * Exported for unit tests so the mapping can be asserted without
 * a DOM.
 */
export function openMessageImagePreview(img: ImageBlock): void {
  const url = img.url ?? "";
  if (!url) return;
  openAttachmentPreviewOverlay(
    {
      id: `msg-image-${Date.now().toString(36)}`,
      messageId: "transcript",
      kind: "image",
      mime: "image/*",
      filename: img.alt ?? url,
      sizeBytes: 0,
      url,
    },
    { mode: "post-send" },
  );
}

/**
 * JAISIU-4442 — transcript audio player.
 *
 * A voice note in the transcript used to be a bare native
 * `<audio controls>` strip: no readable elapsed / total time, no
 * full-width scrubber, and the native chrome auto-hides on idle.
 *
 * The player now mirrors the video progress architecture
 * (JAISIU-4437): an always-visible, full-width, seekable progress
 * track plus an explicit play / pause button and a volume slider,
 * all driven by the `<audio>` element's own live state.
 *
 * The native `controls` stay enabled so the existing browser
 * behaviour is untouched; the custom bar is an addition.
 */
/**
 * JAISIU-4447 — stable key for a transcript audio block.
 *
 * Prefers the block's own id and otherwise derives one from the URL,
 * so the same voice note always keys the same pending-seek state.
 */
function audioId(audio: AudioBlock): string {
  if (audio.id) return audio.id;
  return `transcript-audio:${audio.url}`;
}

function renderMessageAudios(audios: AudioBlock[]) {
  if (audios.length === 0) {
    return nothing;
  }

  return html`
    <div class="chat-message-audios">
      ${audios.map(
        (audio) => html`
          <div class="chat-message-audio-scope" data-testid="chat-message-audio-scope">
            <audio
              src=${audio.url}
              preload="metadata"
              class="chat-message-audio"
              data-testid="chat-message-audio"
              data-attachment-id=${audioId(audio)}
              @loadedmetadata=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @durationchange=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @timeupdate=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @seeked=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @play=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @pause=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @ended=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
              @volumechange=${(e: Event) => {
                syncTranscriptAudioProgress(e.currentTarget as HTMLAudioElement);
              }}
            ></audio>
            ${renderAudioProgressBar(
              (track: HTMLElement) => transcriptAudioMediaFor(track, "chat-message-audio"),
              (seconds: number, track: HTMLElement) => {
                const media = transcriptAudioMediaFor(track, "chat-message-audio");
                if (!media) return;
                requestAttachmentSeek(media.dataset.attachmentId ?? "", seconds);
                applySeek(media, seconds);
              },
              "chat-message-audio-progress",
            )}
          </div>
        `,
      )}
    </div>
  `;
}

/**
 * Resolve the `<audio>` element that owns the given transcript
 * progress track.
 *
 * The track and the media element are siblings inside the same
 * `.chat-message-audio-scope` wrapper, so the lookup stays local to
 * the player instead of reaching into the whole document — two voice
 * notes in the same transcript never interfere.
 */
function transcriptAudioMediaFor(track: HTMLElement, testid: string): HTMLAudioElement | null {
  const scope = track.closest(".chat-message-audio-scope") as HTMLElement | null;
  const scoped = scope?.querySelector(`[data-testid="${testid}"]`) as HTMLAudioElement | null;
  if (scoped) return scoped;
  return (document.querySelector(`[data-testid="${testid}"]`) ?? null) as HTMLAudioElement | null;
}

/**
 * JAISIU-4447 — refresh a transcript audio progress bar from the
 * media element's own events.
 *
 * Same contract as the overlay's `syncAudioProgress`: the PENDING
 * seek wins over a stale media position until the browser lands it,
 * so a click on the track does not snap the playhead back to where
 * it was.
 */
function syncTranscriptAudioProgress(media: HTMLAudioElement): void {
  if (!media) return;
  const attachmentId = media.dataset.attachmentId ?? "";
  const resolved = resolveAttachmentPlaybackTime(attachmentId, media.currentTime);
  updateAudioProgressBar(media, resolved);
}

/**
 * JAISIU-4428 — the render kind for a transcript session-file record.
 *
 * Prefers the server's `kind` (the gateway's `SessionFileKind` family:
 * `image | video | audio | document | tabular | text | binary`) and maps it
 * onto the UI's {@link AttachmentKind} vocabulary, so the chip, the
 * `data-attachment-kind` attribute, the overlay, and the icon all agree.
 * Falls back to the MIME when the record has no `kind` — historical rows
 * written before `kind` existed still carry a MIME.
 *
 * Why this replaces the old `file.mediaType.startsWith("image/")` test:
 * that boolean collapsed video, audio, and PDF into the same
 * download-only chip (JAISIU-4425 defect D6), so the operator saw a
 * download arrow where a thumbnail or player belonged.
 */
export function transcriptFileRenderKind(file: Partial<TranscriptSessionFile>): AttachmentKind {
  // Server kind first — it survives a re-upload where the browser
  // supplied a sloppy `application/octet-stream` MIME.
  switch (file.kind) {
    case "image":
      return "image";
    case "video":
      return "video";
    case "audio":
      return "audio";
    case "document":
      // The gateway's `document` family is coarse: it holds PDFs *and*
      // office documents. The MIME is the finer signal, so it decides
      // between the render kinds the overlay can actually draw:
      //
      //   - `application/pdf` → `pdf` (the iframe body, not text)
      //   - a spreadsheet MIME → `spreadsheet` (the prepared table)
      //   - anything else → `document` (the extracted text)
      //
      // JAISIU-4451 — the spreadsheet branch matters: a `.xlsx` the
      // gateway filed under `document` (it does so whenever the stored
      // MIME was not one of the tabular ones) used to open the
      // *document* body and show "not analysed" instead of the
      // prepared table.
      return documentFamilyRenderKind(file.mediaType ?? "");
    case "tabular":
      return "spreadsheet";
    case "text":
      return "code";
    case "binary":
      return "file";
    default:
      break;
  }
  return attachmentKindForMime(file.mediaType ?? "");
}

/**
 * JAISIU-4451 — the render kind for a file in the gateway's coarse
 * `document` family.
 *
 * The gateway classifies PDFs, Word documents and (occasionally)
 * spreadsheets all as `document`. The overlay draws three different
 * bodies for those, so the MIME has to pick the right one:
 *
 *   - `application/pdf` → `pdf` — the `<iframe>` body.
 *   - a spreadsheet MIME (xlsx / xls / ods / csv) → `spreadsheet` —
 *     the prepared tabular table.
 *   - everything else → `document` — the extracted text.
 *
 * Split out of {@link transcriptFileRenderKind} so it can be unit
 * tested on its own and reused by the batch builder.
 */
export function documentFamilyRenderKind(mediaType: string): AttachmentKind {
  const mime = (mediaType ?? "").toLowerCase().trim();
  if (mime === "application/pdf") return "pdf";
  // A spreadsheet MIME always means the tabular body, even when the
  // gateway filed the record under `document`.
  if (attachmentKindForMime(mime) === "spreadsheet") return "spreadsheet";
  return "document";
}

/**
 * JAISIU-4451 — the overlay's navigation batch for a message's
 * attachments.
 *
 * ## Why the batch is *not* the raw file list
 *
 * The overlay renders a body per attachment, and only the
 * previewable kinds have one (`image`, `pdf`, `video`, `audio`,
 * `spreadsheet`, `document`). A `code` / `file` member has no body:
 * navigating to it paints the download-fallback panel, so the
 * operator loses the preview they were walking through.
 *
 * The batch therefore holds **only the previewable attachments**, in
 * the order they appear in the message. ← / → then traverses exactly
 * the set the ticket describes: "every previewable attachment in a
 * mixed message batch".
 *
 * Download stays a separate action: the chip keeps its own download
 * anchor, and the overlay header keeps its Download button — neither
 * is affected by the batch's composition.
 *
 * Pure: no DOM, no I/O. The URL resolver is injected so the module
 * stays free of host knowledge.
 */
export interface PreviewableBatch {
  /** The previewable attachments, in message order. */
  batch: ChatAttachment[];
  /**
   * The index of each file inside {@link batch}, keyed by file id.
   * A file that is *not* previewable is absent from the map (it has
   * no place in the batch).
   */
  indexByFileId: Map<string, number>;
}

/**
 * JAISIU-4451 — build the overlay navigation batch for a message's
 * session files.
 *
 * @param files     the message's session-file records, in render order
 * @param resolveUrl optional resolver for a file's raw URL (the same
 *                  one the chip uses)
 */
export function buildPreviewableBatch(
  files: TranscriptSessionFile[],
  resolveUrl?: (file: TranscriptSessionFile) => string,
): PreviewableBatch {
  const batch: ChatAttachment[] = [];
  const indexByFileId = new Map<string, number>();
  for (const file of files) {
    const kind = transcriptFileRenderKind(file);
    // A non-previewable kind (code / file) has no overlay body — it
    // is not part of the navigation set.
    if (!attachmentIsPreviewable({ kind })) continue;
    indexByFileId.set(file.fileId, batch.length);
    batch.push({
      id: file.fileId,
      filename: file.filename,
      mime: file.mediaType,
      kind,
      sizeBytes: file.size,
      url: resolveUrl?.(file) ?? "",
    });
  }
  return { batch, indexByFileId };
}

/**
 * JAISIU-4428 — pick the `src` for an inline image/video thumbnail.
 *
 * Defect D5: the host's `resolvePreviewUrl` always returns
 * `/api/session/files/<id>/preview`, but the gateway 404s that route
 * whenever it never wrote a derivative — `writePreviewDerivative` returns
 * `null` for any non-image kind, and for images whenever `sharp` is
 * missing or fails to decode. A PNG then rendered as an invisible chip
 * while the bytes sat reachable at `/raw`.
 *
 * Rule:
 *   - no preview resolver → `raw` (correct, just not downscaled);
 *   - record has no `previewMediaType` → the derivative provably does not
 *     exist, so `raw`;
 *   - record has `previewMediaType` → the derivative exists, so `preview`;
 *   - and regardless, the caller also installs an `onerror` fallback so a
 *     derivative that vanishes later still recovers.
 */
function resolveSessionFilePreviewSrc(
  file: TranscriptSessionFile,
  raw: string,
  resolvePreviewUrl?: (file: TranscriptSessionFile) => string,
): { src: string; fallbackTo?: string } {
  if (!resolvePreviewUrl) return { src: raw };
  if (!file.previewMediaType) return { src: raw };
  const preview = resolvePreviewUrl(file);
  // A resolver that returns an empty string is not a usable preview URL.
  if (!preview) return { src: raw };
  return { src: preview, ...(preview !== raw ? { fallbackTo: raw } : {}) };
}

/**
 * JAISIU-4428 — the `ChatAttachment` the overlay receives for a transcript
 * file. `kind` must be the *render* kind, not the gateway family, or the
 * overlay picks the wrong body (or refuses to open at all).
 */
/**
 * JAISIU-4449 — adapt the host's file-keyed preview loader to the
 * overlay's attachment-keyed one.
 *
 * The host resolves by session-file record (it knows the credentials);
 * the overlay asks by attachment (it knows which chip is open). The
 * overlay's attachment id *is* the session file id for a transcript
 * file, so the bridge is a direct lookup.
 */
function previewLoaderFor(
  load: (file: TranscriptSessionFile) => Promise<ChatAttachment | null>,
): (a: ChatAttachment) => Promise<ChatAttachment | null> {
  return async (a: ChatAttachment) => {
    const fileId = a.sessionFileId ?? a.id;
    if (!fileId) return null;
    const loaded = await load({
      fileId,
      filename: a.filename ?? a.name ?? "",
      mediaType: a.mime ?? a.mimeType ?? "",
      size: a.sizeBytes ?? a.size ?? 0,
    } as TranscriptSessionFile);
    if (!loaded) return null;
    // The host resolves the preview payload; the overlay still owns the
    // attachment's identity, url and kind.
    return {
      ...a,
      ...loaded,
      id: a.id,
      url: a.url ?? loaded.url,
      kind: a.kind ?? loaded.kind,
    };
  };
}

/**
 * JAISIU-4428 — the `ChatAttachment` the overlay receives for a transcript
 * file. `kind` must be the *render* kind, not the gateway family, or the
 * overlay picks the wrong body (or refuses to open at all).
 */
function transcriptFileToOverlayAttachment(
  file: TranscriptSessionFile,
  kind: AttachmentKind,
  raw: string,
): ChatAttachment {
  return {
    id: file.fileId,
    messageId: file.fileId,
    kind,
    mime: file.mediaType,
    filename: file.filename,
    sizeBytes: file.size,
    url: raw,
    ...(file.sha256 ? { sha256: file.sha256 } : {}),
  };
}

/**
 * JAISIU-4428 — open the unified overlay for a transcript attachment,
 * honouring modifier-clicks so ⌘-click / middle-click still open the raw
 * bytes in a new tab the way users expect.
 */
function openTranscriptFileOverlay(
  ev: MouseEvent,
  file: TranscriptSessionFile,
  kind: AttachmentKind,
  raw: string,
  nav?: {
    batch: ChatAttachment[];
    batchIndex: number;
    /** JAISIU-4449 — load the prepared preview (tabular / text). */
    loadPreview?: (a: ChatAttachment) => Promise<ChatAttachment | null>;
  },
): void {
  if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button === 1) {
    return;
  }
  ev.preventDefault();
  openAttachmentPreviewOverlay(transcriptFileToOverlayAttachment(file, kind, raw), {
    mode: "post-send",
    // JAISIU-4430 — keep the multi-attachment nav that JAISIU-4427 added
    // so an operator can walk a mixed batch with ←/→ without closing the
    // overlay between files.
    ...(nav ?? {}),
  });
}

/**
 * JAISIU-4428 — the inline media element for a sent attachment.
 *
 * Replaces the old image-or-icon fork:
 *   - **image** → `<img>` thumbnail (preview URL with a raw fallback);
 *   - **video** → `<video>` poster/thumbnail frame, no `autoplay`; the
 *     overlay carries the real player. `playsinline` keeps iOS Safari from
 *     throwing the whole chip into the system fullscreen player;
 *   - **audio** → a compact inline `<audio controls>` so a voice note is
 *     playable in place;
 *   - **pdf / spreadsheet / document** → no media element. The overlay
 *     owns those bodies (iframe / extracted HTML); a chip-only
 *     presentation with a distinct icon is the honest inline state, and a
 *     non-previewable kind keeps the type icon.
 */
function renderInlineAttachmentMedia(
  file: TranscriptSessionFile,
  kind: AttachmentKind,
  preview: { src: string; fallbackTo?: string },
): TemplateResult | typeof nothing {
  if (kind === "image") {
    return html`<img
      class="chat-message-file__img"
      src=${preview.src}
      alt=${`Attachment: ${file.filename}`}
      loading="lazy"
      decoding="async"
      data-testid="chat-message-file-img"
      data-attachment-kind="image"
      @error=${(ev: Event) => {
        // D5 — a preview derivative can 404 even when the record claimed
        // one (derivative pruned, `sharp` unavailable at render time).
        // Rewrite to the raw bytes rather than leaving a broken image.
        const img = ev.currentTarget as HTMLImageElement | null;
        if (!img || !preview.fallbackTo || img.dataset.fallbackApplied === "1") return;
        img.dataset.fallbackApplied = "1";
        img.src = preview.fallbackTo;
      }}
    />`;
  }
  if (kind === "video") {
    return html`<span
      class="chat-message-file__media chat-message-file__media--video"
      data-testid="chat-message-file-video-wrap"
    >
      <video
        class="chat-message-file__video"
        src=${preview.src}
        preload="metadata"
        playsinline
        muted
        data-testid="chat-message-file-video"
        data-attachment-kind="video"
        @error=${(ev: Event) => {
          const v = ev.currentTarget as HTMLVideoElement | null;
          if (!v || !preview.fallbackTo || v.dataset.fallbackApplied === "1") return;
          v.dataset.fallbackApplied = "1";
          v.src = preview.fallbackTo;
        }}
      ></video>
      <span class="chat-message-file__play" aria-hidden="true">▶</span>
    </span>`;
  }
  if (kind === "audio") {
    return html`<span
      class="chat-message-file__audio-scope chat-message-audio-scope"
      data-testid="chat-message-file-audio-wrap"
    >
      <audio
        class="chat-message-file__audio"
        src=${preview.src}
        preload="metadata"
        data-testid="chat-message-file-audio"
        data-attachment-kind="audio"
        data-attachment-id=${file.fileId}
        @error=${(ev: Event) => {
          const a = ev.currentTarget as HTMLAudioElement | null;
          if (!a || !preview.fallbackTo || a.dataset.fallbackApplied === "1") return;
          a.dataset.fallbackApplied = "1";
          a.src = preview.fallbackTo;
        }}
        @loadedmetadata=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @durationchange=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @timeupdate=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @seeked=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @play=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @pause=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @ended=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
        @volumechange=${(ev: Event) => {
          syncTranscriptAudioProgress(ev.currentTarget as HTMLAudioElement);
        }}
      ></audio>
      ${renderAudioProgressBar(
        (track: HTMLElement) => transcriptAudioMediaFor(track, "chat-message-file-audio"),
        (seconds: number, track: HTMLElement) => {
          const media = transcriptAudioMediaFor(track, "chat-message-file-audio");
          if (!media) return;
          requestAttachmentSeek(media.dataset.attachmentId ?? "", seconds);
          applySeek(media, seconds);
        },
        "chat-message-file-audio-progress",
      )}
    </span>`;
  }
  return nothing;
}

/**
 * JAISIU-4428 — `renderInlineAttachmentMedia` returns `nothing` for the
 * kinds with no inline media element (PDF / spreadsheet / document /
 * code / file). Lit renders `nothing` as an empty comment node, but the
 * chip still needs the type icon beside it, so collapse that case to
 * `undefined` and let the caller fall through with `||`.
 */
function previewableMedia(
  file: TranscriptSessionFile,
  kind: AttachmentKind,
  preview: { src: string; fallbackTo?: string },
): TemplateResult | undefined {
  const media = renderInlineAttachmentMedia(file, kind, preview);
  return media === nothing ? undefined : media;
}

/**
 * Render the files a user attached to a message.
 *
 * A text-only bubble after an attach is a bug: the operator dropped a file,
 * the file was stored, and the transcript must show it. Images get a real
 * thumbnail from the gateway's preview endpoint; everything else gets a
 * type icon with the filename and size. Every row links to the session
 * file's bytes so the click-through resolves to the same content the model
 * was given.
 *
 * Click contract (JAISIU-4148 — post-send visual rendering):
 *   - **image**  → opens the unified preview overlay (`openAttachmentPreviewOverlay`)
 *                  at the **raw** URL so the original file loads at full
 *                  resolution. The overlay's own header carries a Download
 *                  button. A dedicated download anchor is also rendered on
 *                  the chip itself, so the operator can save the file
 *                  without opening the overlay first.
 *                  ⌘-click / middle-click still open the raw URL in a new
 *                  tab the way users expect.
 *   - **non-previewable** (`code` / `file`) → `download` attribute is set
 *                     so the browser saves the bytes to disk instead of
 *                     navigating the tab away from the chat. The href
 *                     points at the raw bytes; right-click "Save link as"
 *                     resolves to the same file. A dedicated download
 *                     anchor is also visible on the chip for clarity.
 *
 * JAISIU-4428 — every previewable kind now opens the overlay, not just
 * images: video shows a thumbnail frame, audio a compact player, PDF /
 * spreadsheet / document a kind-specific icon that still opens the overlay.
 * See {@link transcriptFileRenderKind} and {@link renderInlineAttachmentMedia}.
 */
function renderSessionFiles(
  files: TranscriptSessionFile[],
  opts: {
    resolveUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4148 — resolver for the gateway preview derivative
     * (downscaled thumbnail). When supplied, the chip's `<img src>`
     * points at this URL directly; when omitted, the chip falls back
     * to `resolveUrl` so the same URL is shown as the original (still
     * a valid image, just unoptimised). This removes the previous
     * `/raw`→`/preview` string-replace workaround that broke on
     * signed/CDN URL shapes.
     */
    resolvePreviewUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4449 — load the *prepared preview* of a stored session
     * file (tabular schema + sample rows for a spreadsheet, extracted
     * text for a document). Supplied by the host so this module stays
     * free of credentials/origin knowledge. Optional — without it the
     * overlay keeps the honest "not analysed" message.
     */
    loadPreview?: (a: ChatAttachment) => Promise<ChatAttachment | null>;
  },
) {
  if (files.length === 0) {
    return nothing;
  }
  return html`
    <div class="chat-message-files" data-testid="chat-message-files">
      ${files.map((file) => {
        const raw = opts.resolveUrl?.(file) ?? "";
        // JAISIU-4428 — one kind for the chip, the `data-attachment-kind`
        // attribute, the icon, the inline media element, and the overlay.
        const kind = transcriptFileRenderKind(file);
        const preview = resolveSessionFilePreviewSrc(file, raw, opts.resolvePreviewUrl);
        // Only a kind the overlay can render opens it; `code`/`file` keep
        // the download behaviour they always had.
        const isPreviewable = attachmentIsPreviewable({ kind });
        const downloadName = file.filename || "attachment";
        const targetValue = isPreviewable ? null : "_blank";
        const relValue = isPreviewable ? null : "noopener noreferrer";
        const ariaLabel = isPreviewable
          ? `View attached ${kind}: ${file.filename}`
          : `Download attachment: ${file.filename} (${formatAttachmentBytes(file.size)})`;
        // JAISIU-4451 — the overlay's navigation batch holds *only*
        // the previewable attachments of this message, so ← / →
        // traverses every previewable kind (image, PDF, video, audio,
        // spreadsheet, document) and never lands on a download-only
        // member. The order follows the message.
        const { batch, indexByFileId } = buildPreviewableBatch(files, opts.resolveUrl);
        const batchIndex = indexByFileId.get(file.fileId) ?? 0;
        const overlayAttachment: ChatAttachment = {
          id: file.fileId,
          messageId: file.fileId,
          kind,
          mime: file.mediaType,
          filename: file.filename,
          sizeBytes: file.size,
          url: raw,
          ...(file.sha256 ? { sha256: file.sha256 } : {}),
        };
        return html`
          <span
            class="chat-message-file"
            data-testid="chat-message-file"
            data-session-file-id=${file.fileId}
            data-attachment-kind=${kind}
            title=${`${file.filename} · ${formatAttachmentBytes(file.size)}${
              file.mediaType ? ` · ${file.mediaType}` : ""
            }${isPreviewable ? " · click to view" : " · click to download"}`}
          >
            <a
              class="chat-message-file__link"
              href=${raw}
              download=${isPreviewable ? nothing : downloadName}
              target=${targetValue ?? nothing}
              rel=${relValue ?? nothing}
              aria-label=${ariaLabel}
              data-testid="chat-message-file-link"
              data-session-file-id=${file.fileId}
              data-attachment-kind=${kind}
              @click=${(ev: MouseEvent) => {
                if (!isPreviewable) return;
                // Honour modifier-clicks so power users keep "open in
                // new tab" via ⌘-click / middle-click / right-click.
                openTranscriptFileOverlay(ev, file, kind, raw, {
                  batch,
                  batchIndex,
                  ...(opts.loadPreview ? { loadPreview: opts.loadPreview } : {}),
                });
              }}
            >
              ${
                previewableMedia(file, kind, preview) ||
                html`<span
                  class="chat-message-file__icon ${attachmentIconColorClassForAttachment({
                    kind,
                    mimeType: file.mediaType,
                    filename: file.filename,
                  })}"
                  data-testid="chat-message-file-icon"
                  data-attachment-icon-variant=${attachmentIconColorClassForAttachment({
                    kind,
                    mimeType: file.mediaType,
                    filename: file.filename,
                  })}
                  aria-hidden="true"
                >
                  ${attachmentIconForRenderKind(kind, {
                    mimeType: file.mediaType,
                    filename: file.filename,
                  })}
                </span>`
              }
              <span class="chat-message-file__meta">
                <span class="chat-message-file__name">${file.filename}</span>
                <span class="chat-message-file__sub">
                  ${[formatAttachmentBytes(file.size), mediaTypeLabel(file.mediaType)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </a>
            <a
              class="chat-message-file__download"
              href=${raw}
              download=${downloadName}
              target="_blank"
              rel="noopener noreferrer"
              aria-label=${`Download ${file.filename}`}
              data-testid="chat-message-file-download"
              data-session-file-id=${file.fileId}
              title=${`Download ${file.filename}`}
              @click=${(ev: MouseEvent) => ev.stopPropagation()}
              >${icons.arrowDown}</a
            >
          </span>
        `;
      })}
    </div>
  `;
}

/**
 * JAISIU-3993: render outbound attachment chips for files the AGENT
 * produced in this turn (gen-ai image / video / audio). The chip DOM
 * mirrors the existing `renderSessionFiles` shape (one anchor per
 * file, filename + size + mime meta, image-preview when the kind
 * allows) so the test selectors work and the styling is consistent.
 *
 * Why a separate function (not just `renderSessionFiles` for both):
 * the assistant side has its own `data-testid` so a future test can
 * assert "the agent attached exactly one PNG" without false-positives
 * on user-side uploads. The data attribute also lets the click-handler
 * later route to the in-app viewer without confusion.
 */
function renderOutboundAttachmentChips(
  files: TranscriptSessionFile[],
  opts: {
    resolveUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4148 — resolver for the gateway preview derivative.
     * See {@link renderSessionFiles} for the full rationale. Same
     * rule: when supplied, the chip's `<img src>` uses it directly;
     * otherwise falls back to `resolveUrl`.
     */
    resolvePreviewUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4449 — load the *prepared preview* of a stored session
     * file. See {@link renderSessionFiles}.
     */
    loadPreview?: (a: ChatAttachment) => Promise<ChatAttachment | null>;
  },
) {
  if (files.length === 0) {
    return nothing;
  }
  return html`
    <div class="chat-message-files" data-testid="chat-outbound-attachments">
      ${files.map((file) => {
        const raw = opts.resolveUrl?.(file) ?? "";
        // JAISIU-4428 — same kind resolution as the inbound chip, so an
        // agent-produced mp4/png/mp3 renders identically to a user-sent one.
        const kind = transcriptFileRenderKind(file);
        const preview = resolveSessionFilePreviewSrc(file, raw, opts.resolvePreviewUrl);
        const isPreviewable = attachmentIsPreviewable({ kind });
        // JAISIU-4430 — keep the outbound multi-attachment nav batch.
        // JAISIU-4451 — built from the previewable attachments only,
        // exactly like the inbound transcript chips.
        const { batch, indexByFileId } = buildPreviewableBatch(files, opts.resolveUrl);
        const batchIndex = indexByFileId.get(file.fileId) ?? 0;
        return html`
          <span
            class="chat-message-file"
            data-testid="outbound-attachment-chip"
            data-file-id=${file.fileId}
            data-session-file-id=${file.fileId}
            data-attachment-kind=${kind}
            title=${`${file.filename} · ${formatAttachmentBytes(file.size)}${
              file.mediaType ? ` · ${file.mediaType}` : ""
            }`}
          >
            <a
              class="chat-message-file__link"
              href=${raw}
              data-testid="outbound-attachment-chip-link"
              data-file-id=${file.fileId}
              data-session-file-id=${file.fileId}
              data-attachment-kind=${kind}
              @click=${(ev: MouseEvent) => {
                if (!isPreviewable) return;
                openTranscriptFileOverlay(ev, file, kind, raw, {
                  batch,
                  batchIndex,
                  ...(opts.loadPreview ? { loadPreview: opts.loadPreview } : {}),
                });
              }}
            >
              ${
                previewableMedia(file, kind, preview) ||
                html`<span
                  class="chat-message-file__icon ${attachmentIconColorClassForAttachment({
                    kind,
                    mimeType: file.mediaType,
                    filename: file.filename,
                  })}"
                  data-testid="outbound-attachment-chip-icon"
                  data-attachment-icon-variant=${attachmentIconColorClassForAttachment({
                    kind,
                    mimeType: file.mediaType,
                    filename: file.filename,
                  })}
                  aria-hidden="true"
                >
                  ${attachmentIconForRenderKind(kind, {
                    mimeType: file.mediaType,
                    filename: file.filename,
                  })}
                </span>`
              }
              <span class="chat-message-file__meta">
                <span class="chat-message-file__name">${file.filename}</span>
                <span class="chat-message-file__sub">
                  ${[formatAttachmentBytes(file.size), mediaTypeLabel(file.mediaType)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </a>
            <a
              class="chat-message-file__download"
              href=${raw}
              download=${file.filename || "attachment"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label=${`Download ${file.filename}`}
              data-testid="outbound-attachment-chip-download"
              data-file-id=${file.fileId}
              title=${`Download ${file.filename}`}
              @click=${(ev: MouseEvent) => ev.stopPropagation()}
              >${icons.arrowDown}</a
            >
          </span>
        `;
      })}
    </div>
  `;
}

/**
 * JAISIU-4435 — the local `kindFromMediaType` helper is gone.
 *
 * It mapped `application/pdf` to the gateway's coarse `"document"`, which
 * is exactly the kind of lossy guess that made a PDF render with the
 * wrong icon and the wrong preview body. The render kind now comes from
 * {@link transcriptFileRenderKind}, which resolves the MIME first.
 */

/**
 * JAISIU-4018 (chat attachment previews) — render an inline text-preview
 * card for every session file that the {@link attachmentEligibleForInlinePreview}
 * gate accepts. The card is the `chat-attachment-inline-preview` custom element
 * (`./chat-attachment-inline-preview.ts`); it owns its own extraction state,
 * progress, retry, and sessionStorage persistence.
 *
 * Renders nothing for binary attachments (image / video / audio / PDF) — those
 * are still best handled by the existing `attachment-preview-overlay.ts` lightbox.
 */
function renderAttachmentInlinePreviews(
  files: TranscriptSessionFile[],
  opts: {
    resolveUrl?: (file: TranscriptSessionFile) => string;
    onSummarize?: InlinePreviewOptions["onSummarize"];
    onUseInComposer?: InlinePreviewOptions["onUseInComposer"];
  },
) {
  const eligible = files.filter((f) =>
    attachmentEligibleForInlinePreview({ mediaType: f.mediaType }),
  );
  if (eligible.length === 0) return nothing;
  return html`
    <div class="chat-message-inline-previews" data-testid="chat-message-inline-previews">
      ${eligible.map((file) => {
        const previewFile: InlinePreviewFile = {
          fileId: file.fileId,
          filename: file.filename,
          mediaType: file.mediaType,
          size: file.size,
          ...(file.sha256 ? { sha256: file.sha256 } : {}),
        };
        const previewOpts: InlinePreviewOptions = {
          ...(opts.resolveUrl
            ? {
                resolveUrl: (f) =>
                  opts.resolveUrl?.({
                    fileId: f.fileId,
                    filename: f.filename,
                    mediaType: f.mediaType,
                    size: f.size,
                    ...(f.sha256 ? { sha256: f.sha256 } : {}),
                  }) ?? "",
              }
            : {}),
          onOpenOverlay: (f) => {
            // Same imperative entry as the chip click — re-uses the
            // unified overlay (image / PDF / fallback download).
            // JAISIU-4146: pass the resolved URL through so the
            // overlay's body is the original file, not a
            // placeholder.
            //
            // JAISIU-4451 — the nav batch holds the *previewable*
            // attachments of this message only, exactly like the
            // transcript chips. The inline card itself is for text
            // files (which have no preview body), so it opens the
            // overlay on the file the operator picked and lets ← / →
            // walk the previewable members.
            const url =
              opts.resolveUrl?.({
                fileId: f.fileId,
                filename: f.filename,
                mediaType: f.mediaType,
                size: f.size,
                ...(f.sha256 ? { sha256: f.sha256 } : {}),
              }) ?? "";
            const { batch, indexByFileId } = buildPreviewableBatch(files, opts.resolveUrl);
            const picked = files.find((bf) => bf.fileId === f.fileId);
            const pickedKind = picked ? transcriptFileRenderKind(picked) : "file";
            const pickedAttachment: ChatAttachment = {
              id: f.fileId,
              filename: f.filename,
              mime: f.mediaType,
              kind: pickedKind,
              sizeBytes: f.size,
              url,
            };
            openAttachmentPreviewOverlay(pickedAttachment, {
              mode: "post-send",
              batch,
              batchIndex: indexByFileId.get(f.fileId) ?? 0,
            });
          },
          ...(opts.onSummarize ? { onSummarize: opts.onSummarize } : {}),
          ...(opts.onUseInComposer ? { onUseInComposer: opts.onUseInComposer } : {}),
        };
        // The element is registered via `@customElement` in
        // `chat-attachment-inline-preview.ts`. We set the file and
        // options as properties (not attributes) so the resolver
        // closures and callbacks land in JS land, not string land.
        return html`<chat-attachment-inline-preview
          .file=${previewFile}
          .options=${previewOpts}
        ></chat-attachment-inline-preview>`;
      })}
    </div>
  `;
}

function renderToolCardsForMessage(
  toolCards: ReturnType<typeof extractToolCards>,
  onOpenSidebar?: (content: string) => void,
  transcriptMode?: boolean,
) {
  return toolCards.map((card) => {
    if (transcriptMode !== false && shouldUseToolRow(card)) {
      const row = renderToolRow(card, onOpenSidebar);
      if (row) {
        return row;
      }
    }
    return renderToolCardSidebar(card, onOpenSidebar);
  });
}

// ---------- Redacted footnote (Pillar G / Gap 9) ----------

/**
 * Read the `redacted` payload off an assistant transcript message.
 * The gateway's `broadcastChatReplyFallback` stamps this field when
 * `redactReplyParts` produced any redaction warnings — PII matches,
 * forbidden-pattern hits, or a length truncation.
 *
 * Returns `null` when the message was not redacted, so the bubble
 * stays clean and no chip is rendered.
 */
export function extractRedactedFootnote(
  message: unknown,
): { categories: ReadonlyArray<string>; truncated: boolean } | null {
  const m = message as Record<string, unknown> | null;
  if (!m) return null;
  const redacted = (m as { redacted?: unknown }).redacted;
  if (!redacted || typeof redacted !== "object") {
    return null;
  }
  const obj = redacted as Record<string, unknown>;
  const categoriesRaw = obj.categories;
  const truncated = obj.truncated === true;
  if (!Array.isArray(categoriesRaw) || categoriesRaw.length === 0) {
    return null;
  }
  const categories = categoriesRaw.filter((c): c is string => typeof c === "string");
  if (categories.length === 0) {
    return null;
  }
  return { categories, truncated };
}

const REDACTED_CATEGORY_LABELS: Record<string, string> = {
  pii: "PII",
  forbidden: "forbidden patterns",
  truncated: "trimmed",
};

function redactedFootnoteLabel(categories: ReadonlyArray<string>): string {
  const labels = categories.map((c) => REDACTED_CATEGORY_LABELS[c] ?? c);
  if (labels.length === 1) {
    return labels[0] ?? "";
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export function renderRedactedFootnoteChip(footnote: {
  categories: ReadonlyArray<string>;
  truncated: boolean;
}): TemplateResult {
  const categoryLabel = redactedFootnoteLabel(footnote.categories);
  const tooltip =
    footnote.truncated && footnote.categories.includes("truncated")
      ? `Reply was trimmed by the chat guardrail. ${categoryLabel} redaction(s) were applied to remove sensitive content before the message reached you.`
      : `Reply was redacted by the chat guardrail (${categoryLabel}).`;
  return html`
    <span class="chat-redacted-footnote" role="note" title=${tooltip}>
      <span class="chat-redacted-footnote__icon" aria-hidden="true">⛨</span>
      <span class="chat-redacted-footnote__text">Partially redacted — ${categoryLabel}</span>
    </span>
  `;
}

export function renderGroupedMessage(
  message: unknown,
  opts: {
    isStreaming: boolean;
    showReasoning: boolean;
    reasoningExpanded?: boolean;
    transcriptMode?: boolean;
    inlineInRun?: boolean;
    messageActions?: MessageActionHandlers;
    isLastAssistantInThread?: boolean;
    rewindTarget?: MessageRewindTarget | null;
    editingRewindTarget?: MessageRewindTarget | null;
    onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
    onOpenViewer?: (markdown: string, messageId: string) => void;
    /**
     * JAISIU-3551 — set of message ids currently in the manually-collapsed
     * state. The avatar click toggles membership via `onToggleMessageCollapsed`.
     * `undefined` is treated as "no messages collapsed" (safe default).
     */
    collapsedMessageIds?: ReadonlySet<string>;
    /** JAISIU-3551 — toggle handler invoked when the avatar is clicked. */
    onToggleMessageCollapsed?: (messageId: string) => void;
    /**
     * Resolve a stored session file to its gateway URL. Supplied by the
     * host so this module stays free of credentials/origin knowledge.
     */
    resolveSessionFileUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4148 — resolve a stored session file to its gateway
     * **preview** URL (downscaled thumbnail). Used for the `<img src>` on
     * image chips so the thumbnail comes from a URL the resolver
     * actually returns, not a `/raw`→`/preview` string-replace that
     * breaks on signed/CDN/non-default URL shapes. Falls back to
     * `resolveSessionFileUrl` when omitted (backwards compatible).
     */
    resolveSessionFilePreviewUrl?: (file: TranscriptSessionFile) => string;
    /**
     * JAISIU-4449 — load the *prepared preview* of a stored session
     * file (tabular schema + sample rows for a spreadsheet, extracted
     * text for a document). Host-supplied so this module stays free of
     * credentials/origin knowledge. Optional — without it the overlay
     * keeps the honest "not analysed" message.
     */
    loadSessionFilePreview?: (file: TranscriptSessionFile) => Promise<ChatAttachment | null>;
    /**
     * Optional: invoked when the operator clicks "Summarize" on an
     * inline preview card. The host typically enqueues a
     * "summarize <filename>" prompt into the composer.
     */
    onAttachmentSummarize?: (file: InlinePreviewFile, text: string) => void;
    /**
     * Optional: invoked when the operator clicks "Use in composer" on an
     * inline preview card. The host typically copies the extracted
     * text into the composer so the operator can edit before sending.
     */
    onAttachmentUseInComposer?: (file: InlinePreviewFile, text: string) => void;
  },
  onOpenSidebar?: (content: string) => void,
) {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role : "unknown";
  const isToolResult =
    isToolResultMessage(message) ||
    role.toLowerCase() === "tool" ||
    role.toLowerCase() === "toolresult" ||
    role.toLowerCase() === "tool_result" ||
    typeof m.toolCallId === "string" ||
    typeof m.tool_call_id === "string";

  const toolCards = extractToolCards(message);
  const hasToolCards = toolCards.length > 0;
  const redactedFootnote = extractRedactedFootnote(message);
  const images = extractImages(message);
  const hasImages = images.length > 0;
  const { audios } = extractMedias(message);
  const hasAudios = audios.length > 0;
  // Session files attached to this turn. A text-only bubble after an attach
  // is a bug, so these render on every message that carries them.
  const sessionFiles = extractSessionFiles(message);

  const fileChangesPayload = isFileChangesMessage(message)
    ? parseFileChangesPayload(message)
    : null;

  const extractedText = extractTextCached(message);
  const extractedThinking =
    opts.showReasoning && role === "assistant" ? extractThinkingCached(message) : null;
  const assistantError = extractAssistantErrorMessage(message);
  const markdownBase = extractedText?.trim()
    ? extractedText
    : assistantError
      ? `**Error:** ${assistantError}`
      : null;
  const reasoningText = extractedThinking ?? null;
  const hasResultCard = toolCards.some((c) => c.kind === "result" && Boolean(c.text?.trim()));
  const suppressToolBodyMarkdown = isToolResult && hasToolCards && hasResultCard;

  const summaryParts =
    markdownBase && (role === "user" || role === "system")
      ? extractConversationSummary(markdownBase)
      : null;
  const droppedSummary = summaryParts?.summary ?? null;
  const markdownAfterSummary = summaryParts ? summaryParts.body.trim() || null : markdownBase;
  const markdown = suppressToolBodyMarkdown ? null : markdownAfterSummary;
  const canCopyMarkdown = Boolean(markdown?.trim() || droppedSummary);
  const rewindTarget = opts.rewindTarget ?? null;
  const userDraft = isUserMessage(message) ? (markdownAfterSummary ?? markdownBase ?? "") : null;
  const canEditUser =
    isUserMessage(message) && Boolean(rewindTarget) && Boolean(opts.messageActions?.onEditUser);
  const isEditingSource =
    canEditUser &&
    opts.editingRewindTarget &&
    rewindTarget &&
    rewindTargetsEqual(opts.editingRewindTarget, rewindTarget);

  const hasActions = Boolean(
    opts.messageActions && (rewindTarget || canCopyMarkdown || opts.isLastAssistantInThread),
  );
  // Detect the "Question for user" prompt we render as a plain-text
  // questionnaire bubble. The text starts with a `## Question for user`
  // header so the renderer can attach the gray-orange user-prompt
  // styling instead of the regular assistant bubble styling — visually
  // matches the operator's existing prompt bubbles so the chat reads as
  // a continuous conversation.
  const isQuestionPrompt =
    isAssistantMessage(message) &&
    typeof markdownBase === "string" &&
    /^\s*##\s+Question\s+for\s+user\b/i.test(markdownBase);
  // Collect-mode chip: shown on optimistic user bubbles that were sent
  // while a previous run was still active. The flag is set in
  // `sendChatMessage` when `chatRunId != null` and cleared on server
  // reconciliation (see `clearOptimisticPending`). Non-user bubbles
  // never carry the flag, so the check is just a single property read.
  const isQueuedUserMessage =
    isUserMessage(message) && (message as { queued?: boolean }).queued === true;

  const bubbleClasses = [
    "chat-bubble",
    opts.inlineInRun ? "chat-bubble--inline-run" : "",
    canCopyMarkdown || hasActions ? "has-copy" : "",
    isEditingSource ? "chat-bubble--editing-source" : "",
    isQuestionPrompt ? "chat-bubble--question-for-user" : "",
    isQueuedUserMessage ? "chat-bubble--queued" : "",
    opts.isStreaming ? "streaming" : "",
    "fade-in",
  ]
    .filter(Boolean)
    .join(" ");

  if (!markdown && hasToolCards && isToolResult) {
    return html`${renderToolCardsForMessage(toolCards, onOpenSidebar, opts.transcriptMode)}`;
  }

  if (fileChangesPayload) {
    return html`
      <div class="chat-bubble chat-bubble--file-changes fade-in">
        ${renderChatFileChangesSummary(fileChangesPayload, {
          onFileClick: opts.onFileClick,
        })}
      </div>
    `;
  }

  // Summary-only: muted chip, no blue user bubble.
  if (droppedSummary && !markdown && !reasoningText && !hasToolCards && !hasImages && !hasAudios) {
    return html`<div class="chat-dropped-summary-wrap fade-in">
      ${renderDroppedTurnsSummary(droppedSummary)}
    </div>`;
  }

  if (!markdown && !droppedSummary && !reasoningText && !hasToolCards && !hasImages && !hasAudios) {
    return nothing;
  }

  const actionRole = isUserMessage(message)
    ? "user"
    : isAssistantMessage(message)
      ? "assistant"
      : "other";

  const copyMarkdown = markdown?.trim() || droppedSummary || null;

  // JAISIU-3551 — manual collapse via avatar click. State lives in the
  // chat view (collapsedMessageIds + onToggleMessageCollapsed). The key is
  // the stable message id (NOT the markdown content) so a same-id message
  // survives whitespace edits, markdown re-renders, and re-mounts.
  const collapsedMessageId = manualCollapseIdForMessage(message);
  const isManuallyCollapsed = Boolean(
    collapsedMessageId && opts.collapsedMessageIds?.has(collapsedMessageId) === true,
  );

  const bubbleRef: Ref<HTMLElement> = createRef();
  // JAISIU-3326 — keep text selection isolated to the bubble.
  // `stopPropagation` on `selectstart`/`mouseup` ensures that no future
  // global listener on `document` (e.g. an accidental `selectionchange`
  // capture) can redirect the bubble selection into the composer textarea,
  // which lives outside the bubble DOM. We deliberately do NOT call
  // `preventDefault` here — the browser's native Ctrl/Cmd-C must still
  // copy the highlighted substring into the system clipboard untouched.
  const stopBubblePropagation = (e: Event) => e.stopPropagation();
  const messageActionsWithScope: MessageActionHandlers | undefined = opts.messageActions
    ? {
        ...opts.messageActions,
        bubbleScope: () => bubbleRef.value ?? null,
        onToggleCollapse:
          collapsedMessageId && opts.onToggleMessageCollapsed
            ? () => opts.onToggleMessageCollapsed!(collapsedMessageId)
            : opts.messageActions.onToggleCollapse,
      }
    : undefined;
  return html`
    <div
      ${ref(bubbleRef)}
      class="${bubbleClasses}"
      data-testid=${opts.isStreaming ? "webchat-stream-token" : "chat-message"}
      data-message-role=${role}
      @selectstart=${stopBubblePropagation}
      @mouseup=${stopBubblePropagation}
      title=${
        isQueuedUserMessage
          ? "Queued — will land when the agent finishes the current turn"
          : nothing
      }
    >
      ${renderMessageActions({
        role: actionRole,
        markdown: copyMarkdown,
        rewindTarget,
        userDraft,
        isLastAssistant: opts.isLastAssistantInThread,
        isCollapsed: isManuallyCollapsed,
        handlers: messageActionsWithScope,
      })}
      ${renderMessageImages(images)} ${renderMessageAudios(audios)}
      ${renderSessionFiles(sessionFiles, {
        ...(opts.resolveSessionFileUrl ? { resolveUrl: opts.resolveSessionFileUrl } : {}),
        ...(opts.resolveSessionFilePreviewUrl
          ? { resolvePreviewUrl: opts.resolveSessionFilePreviewUrl }
          : {}),
        ...(opts.loadSessionFilePreview
          ? { loadPreview: previewLoaderFor(opts.loadSessionFilePreview) }
          : {}),
      })}
      ${renderAttachmentInlinePreviews(sessionFiles, {
        ...(opts.resolveSessionFileUrl ? { resolveUrl: opts.resolveSessionFileUrl } : {}),
        ...(opts.onAttachmentSummarize ? { onSummarize: opts.onAttachmentSummarize } : {}),
        ...(opts.onAttachmentUseInComposer
          ? { onUseInComposer: opts.onAttachmentUseInComposer }
          : {}),
      })}
      ${
        // JAISIU-3993: render outbound attachment chips for files the
        // AGENT produced in this turn (gen-ai image / video / audio).
        // `extractOutboundAttachments` is the assistant-side parallel
        // of `extractSessionFiles` above.
        (() => {
          const out = extractOutboundAttachments(message);
          if (out.length === 0) return nothing;
          return renderOutboundAttachmentChips(out, {
            ...(opts.resolveSessionFileUrl ? { resolveUrl: opts.resolveSessionFileUrl } : {}),
            ...(opts.resolveSessionFilePreviewUrl
              ? { resolvePreviewUrl: opts.resolveSessionFilePreviewUrl }
              : {}),
            ...(opts.loadSessionFilePreview
              ? { loadPreview: previewLoaderFor(opts.loadSessionFilePreview) }
              : {}),
          });
        })()
      }
      ${droppedSummary ? renderDroppedTurnsSummary(droppedSummary) : nothing}
      ${
        reasoningText
          ? renderReasoningBlock({
              text: reasoningText,
              defaultOpen: opts.reasoningExpanded === true,
            })
          : nothing
      }
      ${
        // JAISIU-3551 — avatar-driven manual collapse. State lives in the
        // chat view (collapsedMessageIds + onToggle). The body block is
        // routed through `renderExpandableMessageBody` so manual collapse
        // and the long-message TL;DR share one renderer and one preview
        // styling surface.
        markdown
          ? renderExpandableMessageBody({
              markdown,
              manuallyCollapsed: isManuallyCollapsed,
              ...(collapsedMessageId ? { messageId: collapsedMessageId } : {}),
              ...(opts.onOpenViewer ? { onOpenViewer: opts.onOpenViewer } : {}),
            })
          : nothing
      }
      ${renderToolCardsForMessage(toolCards, onOpenSidebar, opts.transcriptMode)}
      ${
        // UX-PLAN Pillar G (Gap 9): surface a "Partially redacted" footnote
        // chip when the gateway stamped `message.redacted` on this assistant
        // bubble (PII / forbidden pattern / length cap). The chip is
        // deliberately muted — the bubble still reads naturally — but the
        // tooltip names the category so the operator knows the reply was
        // sanitised.
        redactedFootnote && role === "assistant"
          ? renderRedactedFootnoteChip(redactedFootnote)
          : nothing
      }
      ${
        isQueuedUserMessage
          ? html`<div
              class="chat-queued-chip"
              role="status"
              data-testid="chat-queued-chip"
              aria-live="polite"
            >
              <span class="chat-queued-chip__icon" aria-hidden="true">⏳</span>
              <span class="chat-queued-chip__text"
                >Queued — will land when the agent finishes the current turn</span
              >
            </div>`
          : nothing
      }
    </div>
  `;
}

export type LongMessageSummary = {
  lines: number;
  chars: number;
  size: string;
};

/**
 * Quick stats about a markdown payload — used by the "long message" chip in the
 * chat view so the UI can show "(N lines · 5 KB)" before the user opens the viewer.
 *
 * - `chars` is the raw text length (includes whitespace).
 * - `lines` counts "\n" + 1, with `0` for the empty string.
 * - `size` formats chars as `"<n> characters"` below 1 KiB, `"<n> KB"` above (integer).
 */
// JAISIU-3551 — maximum characters shown in the one-line collapsed
// preview. Sized so a single line of prose at the chat transcript's font
// weight (~13px mono) fits on one row in the widest panel without wrapping.
const COLLAPSED_PREVIEW_MAX_CHARS = 240;

/**
 * JAISIU-3551 — derive a stable id for the manual-collapse state from a
 * message object. Prefers explicit message ids (id / transcriptId /
 * messageId); falls back to a deterministic hash of the message content
 * (so transient or id-less messages can still be collapsed, e.g. the
 * queued/draft user bubbles during composition).
 */
function manualCollapseIdForMessage(message: unknown): string | undefined {
  if (!message || typeof message !== "object") return undefined;
  const record = message as Record<string, unknown>;
  for (const key of ["id", "transcriptId", "messageId"] as const) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  // Fallback: stable hash of the message content so the same message always
  // maps to the same id across re-renders.
  const text = extractTextCached(message) ?? "";
  if (!text.trim()) return undefined;
  return fallbackCollapseId(text);
}

function fallbackCollapseId(text: string): string {
  // FNV-1a — cheap, deterministic, plenty good enough as a Map key.
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return `msg-${hash.toString(36)}`;
}

/**
 * JAISIU-3551 — one-line preview text for the collapsed bubble. Strips
 * markdown syntax noise (headers, fences, bold markers, blockquotes) so the
 * preview reads as natural prose. Appends an ellipsis when truncated.
 */
function truncateForPreview(markdown: string, maxChars: number): string {
  const flat = markdown
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(/`([^`]+)`/g, "$1") // inline code → just the text inside
    .replace(/^#{1,6}\s+/gm, "") // ATX headings
    .replace(/^\s*>\s?/gm, "") // blockquote markers
    .replace(/[*_~]+/g, "") // emphasis markers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // [text](url) → text
    .replace(/\s+/g, " ")
    .trim();
  if (flat.length <= maxChars) return flat;
  return `${flat.slice(0, maxChars).trimEnd()}…`;
}

export function summarizeLongMessage(text: string): LongMessageSummary {
  const chars = text.length;
  const lines = text === "" ? 0 : (text.match(/\n/g)?.length ?? 0) + 1;
  const size = chars < 1024 ? `${chars} characters` : `${Math.round(chars / 1024)} KB`;
  return { lines, chars, size };
}
